/*
 * Copyright (C) 2021 Hal Perkins.  All rights reserved.  Permission is
 * hereby granted to students registered for University of Washington
 * CSE 331 for use solely during Winter Quarter 2021 for purposes of
 * the course.  No other use, copying, distribution, or modification
 * is permitted without prior written consent. Copyrights for
 * third-party components of this work must be honored.  Instructors
 * interested in reusing these course materials should contact the
 * author.
 */

import React, {Component} from 'react';

import "./App.css";
import Map from "./Map";
import PathSelector from "./PathSelector";

import BuildingNames from "./util/BuildingNames";
import Path from "./util/Path";
import SnackAlert from "./SnackAlert";
import GeoTransform from "./util/GeoTransform";

interface AppState {
    buildingNames: BuildingNames | null;    // stores the mappings of short-long buildings
    path: Path                              // stores the current path
    snackMessage: string;                                       // snackbar message
    snackSeverity: 'error' | 'info' | 'success' | 'warning';    // snackbar severity
    snackOpen: boolean;                                         // snackbar open
    latitude: number;   // current x in geographic coordinates, if in range [-90, 90]
    longitude: number;  // current y in geographic coordinates, if in range [-180, 180]
    closestBuildingName: string;
    stepByStep: boolean;    // step-by-step feature on/off
    step: number;           // step-by-step feature current step
    panAndZoom: boolean;     // pan and zoom feature on/off
}

// Hard-coded map stuff.
const CANVAS_WIDTH: number = 4330;
const CANVAS_HEIGHT: number = 2964;

// Hard-coded HTML stuff.
const SPARK_PORT: string = "https://nile-campuspaths.herokuapp.com:443";
const GET_PATH_ENDPOINT: string = "/get-path";
const GET_BUILDINGS_ENDPOINT: string = "/get-building-names";
const GET_CLOSEST_ENDPOINT: string = "/get-closest-building";

// Default app state.
const defaultState: AppState = {
    buildingNames: null,
    path: {
        cost: 0,
        path: [],
        start: {
            x: 0,
            y: 0,
        },
    },
    snackMessage: "",
    snackSeverity: 'info',
    snackOpen: false,
    latitude: 91,   // out of latitude range means not in use
    longitude: 181, // out of longitude range means not in use
    closestBuildingName: "",
    stepByStep: false,
    step: 0,
    panAndZoom: false,
};

class App extends Component<{}, AppState> {

    constructor(props: any) {
        super(props);
        this.state = defaultState;
        this.requestBuildingNames();
    }

    /**
     * Finds the current location of the user and loads it into the app's state.
     */
    loadCurrentLocation = () => {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition((position) => {
                const x: number =
                    GeoTransform.longitudeToPixelCoordinate(position.coords.longitude);
                const y: number =
                    GeoTransform.latitudeToPixelCoordinate(position.coords.latitude);
                if (x < 0 || x > CANVAS_WIDTH || y < 0 || y > CANVAS_HEIGHT) {
                    this.toastAlert("Your location is not on the campus map.",
                        'info');
                } else {
                    this.toastAlert("You have been located.", 'success');
                }
                this.setState(() => {
                    return {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    };
                });
            });
        } else {
            this.toastAlert("Location services are disabled.", 'warning');
        }
    };

    /**
     * Resets the state of the app.
     */
    resetState = () => {
        this.setState(defaultState);
        this.requestBuildingNames();
    };

    /**
     * Requests to load in all building names.
     * Loads a complete mapping of short to long names into the app's state
     * if connection to the Campus Map server is online.
     */
    requestBuildingNames = async () => {
        // load in the buildingNames
        try {
            let response = await fetch(SPARK_PORT + GET_BUILDINGS_ENDPOINT);
            if (!response.ok) {
                this.toastAlert("Error: Bad status.", 'error');
                return;
            }
            let names: BuildingNames = await response.json();
            this.setState({
                buildingNames: names
            });
        } catch (e) {
            this.toastAlert("Error: Cannot reach server.", 'error');
            console.log(e);
        }
    };

    /**
     * Requests a path from the Campus Map server indices to start and end buildings.
     * The indices use the app's state to determine which buildings to lookup.
     * Loads the path from the start to end into the app's state
     * if connection to the Campus Map server is online.
     *
     * @param start the index of the start building in the app's state
     * @param end the index of the end building in the app's state
     */
    requestPath = async (start: number, end: number) => {
        try {
            if (this.state.buildingNames !== null) {
                const startShort = start === -2 ?
                    await this.requestClosestBuilding()
                    :
                    Object.keys(this.state.buildingNames)[start];
                if (startShort === "LOCATION_OFF") {
                    this.toastAlert("Location is disabled. " +
                        "Please enable location services to use this feature.", 'error');
                    return;
                }
                const endShort = Object.keys(this.state.buildingNames)[end];
                const query: string = "?start=" + startShort + "&end=" + endShort;
                let response = await fetch(SPARK_PORT + GET_PATH_ENDPOINT + query);
                if (!response.ok) {
                    this.toastAlert("Error: Bad status.", 'error');
                    return;
                }
                let path: Path = await response.json();
                this.setState({
                    path: path
                });
                const startLong = start === -2 ?
                    "the building closest to your location (" + startShort + ")"
                    :
                    Object.values(this.state.buildingNames)[start];
                const endLong = Object.values(this.state.buildingNames)[end];
                this.toastAlert(
                    "Path mapped from " + startLong + " to " + endLong + ".",
                    'success');
            }
        } catch (e) {
            this.toastAlert("Error: Cannot reach server.", 'error');
            console.log(e);
        }
    };

    /**
     * Requests the closest building to the currently stored location of the user.
     * Loads and returns the short name representation of the building
     * if connection to the Campus Map server is online.
     */
    requestClosestBuilding = async () => {
        // check if user enabled location sharing
        // 91, 181 is an illegal geographic coordinate
        try {
            this.loadCurrentLocation();
            if (this.state.latitude === 91 && this.state.longitude === 181) {
                return "LOCATION_OFF";
            }
            const x: number = GeoTransform.longitudeToPixelCoordinate(
                this.state.longitude);
            const y: number = GeoTransform.latitudeToPixelCoordinate(
                this.state.latitude);
            const query: string = "?x=" + x + "&y=" + y;
            let response = await fetch(SPARK_PORT + GET_CLOSEST_ENDPOINT + query);
            if (!response.ok) {
                this.toastAlert("Error: Bad status.", 'error');
                return;
            }
            let closestBuilding: string = await response.json();
            this.setState({
                closestBuildingName: closestBuilding
            });
            return closestBuilding;
        } catch (e) {
            this.toastAlert("Error: Cannot reach server.", 'error');
            console.log(e);
        }
    };

    /**
     * Displays a toast alert with the given message, severity, and optional dialog.
     *
     * @param alertMessage the message to be displayed in a toast
     * @param severity the severity of the toast
     */
    toastAlert = (alertMessage: string, severity: 'error' | 'info' | 'success' | 'warning') => {
        this.setState(() => {
            return {
                snackMessage: alertMessage,
                snackSeverity: severity,
                snackOpen: true,
            }
        });
    };

    /**
     * Closes the current toast notification.
     */
    closeToast = () => {
        this.setState(() => {
            return {
                snackOpen: false
            };
        });
    };

    /**
     * Draws the path from the start building to the end building.
     *
     * @param start     the JSON index of the building to find a path from.
     *                  A negative index represents no valid building.
     * @param end       the JSON index of the building to find a path to.
     *                  A negative index represents no valid building.
     */
    draw = (start: number, end: number) => {
        // callback will only return -1, -2, or a non-negative number
        // in the range [0, Object.entries(buildingNames).length]
        // -1 means no input
        // -2 as start means use current location
        if (start === 0 && end === 0) {
            this.toastAlert("Invalid start or end building.", 'error');
        } else {
            // asserts good input, handles -2 case inside request
            this.requestPath(start, end);
        }
        this.setState(() => {
            return {
                step: 0
            };
        });
    };

    /**
     * Clears the current app component.
     */
    clear = () => {
        this.resetState();
        this.toastAlert("Pathfinder reset.", 'success')
    };

    /**
     * Toggles step-by-step navigation mode.
     */
    toggleStepByStep = () => {
        if (this.state.stepByStep) {
            this.toastAlert("Disabled step-by-step display.", 'info');
        } else {
            this.toastAlert("Enabled step-by-step display. " +
                "Use the buttons to display subsequent segments in the path.", 'info');
        }
        this.setState({
            stepByStep: !this.state.stepByStep
        });
    };

    /**
     * Steps forward in step-by-step navigation mode.
     */
    stepForward = () => {
        this.setState({
            step: this.state.step + 1
        });
    };

    /**
     * Steps backward in step-by-step navigation mode.
     */
    stepBackward = () => {
        this.setState({
            step: this.state.step - 1
        });
    };

    /**
     * Toggles pan and zoom map viewing mode.
     */
    togglePanAndZoom = () => {
        if (this.state.panAndZoom) {
            this.toastAlert("Disabled automatic pan and zoom on map.", 'info');
        } else {
            this.toastAlert("Enabled automatic pan and zoom on map. " +
                "The map will automatically transform to fit to the path.", 'info');
        }
        this.setState({
            panAndZoom: !this.state.panAndZoom
        });
    };

    render() {
        return (
            <div>
                <div style={{margin: "10px"}}>
                    <PathSelector
                        findLocation={this.loadCurrentLocation}
                        buildingNames={this.state.buildingNames}
                        draw={this.draw}
                        clear={this.clear}
                        updateLocation={this.loadCurrentLocation}
                        stepByStep={this.state.stepByStep}
                        toggleStepByStep={this.toggleStepByStep}
                        step={this.state.step}
                        max={this.state.path.path.length}
                        stepForward={this.stepForward}
                        stepBackward={this.stepBackward}
                        panAndZoom={this.state.panAndZoom}
                        togglePanAndZoom={this.togglePanAndZoom}
                    />
                </div>
                <Map
                    path={this.state.path}
                    latitude={this.state.latitude}
                    longitude={this.state.longitude}
                    stepByStep={this.state.stepByStep}
                    step={this.state.step}
                    panAndZoom={this.state.panAndZoom}
                />
                <SnackAlert
                    message={this.state.snackMessage}
                    severity={this.state.snackSeverity}
                    open={this.state.snackOpen}
                    onChange={this.closeToast}
                    dialogMessage={""}
                    dialogTitle={""}
                />
            </div>
        );
    }

}

export default App;
